import React, { useEffect, useState } from 'react';
import { Table, Tag, Space, Button, Dropdown, Menu } from 'antd';
import { Link } from 'react-router-dom';
import { ProjectPlansApi, ProjectsApi } from 'api/api';
import { CreateProjectPlanCommand, ProjectPlanDto, ProjectPlanStatus, ClientProjectPlansDto } from 'api/models';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import openNotificationWithIcon from 'utility/notification';
import { formatDate } from 'utility/utility';
import { getPriorityColor, getPriorityName, getProjectPlanStatusColor, getProjectPlanStatusName } from '../constants';
import AddProjectPlanModal from './AddProjectPlanModal';
import dayjs from 'dayjs';
interface ProjectPlansTableProps {
  projectId: number | undefined;
  hideClientColumn?: boolean;
  hideAddButton?: boolean;
  initialPlans?: (ProjectPlanDto | ClientProjectPlansDto)[];
}

const projectPlansApi = new ProjectPlansApi();

export const ProjectPlansTable: React.FC<ProjectPlansTableProps> = ({
  projectId,
  hideClientColumn = false,
  hideAddButton = false,
  initialPlans = [],
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<(ProjectPlanDto | ClientProjectPlansDto)[]>(initialPlans);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);

  const fetchPlans = async () => {
    if (projectId && !initialPlans?.length) {
      try {
        setLoading(true);
        const response = await projectPlansApi.apiProjectsProjectIdPlansGet({ projectId: projectId!, pageSize: -1 });
        setPlans(response.data.items || []);
      } catch (err) {
        openNotificationWithIcon('error', t('projects:fetch-plans-error', 'Failed to fetch project plans'));
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [projectId, initialPlans?.length]);

  const handleAddPlan = async (values: CreateProjectPlanCommand) => {
    try {
      await projectPlansApi.apiProjectsProjectIdPlansPost({
        projectId: projectId!,
        createProjectPlanCommand: {
          ...values,
          projectId: projectId!,
          dueDate: values.dueDate ? new Date(values.dueDate).toISOString() : undefined,
        },
      });
      setIsAddModalVisible(false);
      fetchPlans();
      openNotificationWithIcon('success', t('projects:plan-created-success', 'Plan created successfully'));
    } catch (error) {
      openNotificationWithIcon('error', t('projects:plan-created-error', 'Failed to create plan'));
    }
  };

  const columns = [
    {
      title: t('projects:plan-title', 'Title'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('projects:description', 'Description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('projects:unit', 'Unit'),
      dataIndex: 'unitName',
      key: 'unitName',
      render: (unitName: string, record: ProjectPlanDto | ClientProjectPlansDto) =>
        record.unitId ? <Link to={`/units/${record.unitId}`}>{unitName}</Link> : unitName || '-',
    },
    // Conditionally include client column
    ...(!hideClientColumn
      ? [
          {
            title: t('projects:client', 'Client'),
            dataIndex: 'clientName',
            key: 'clientName',
            render: (clientName: string, record: ProjectPlanDto | ClientProjectPlansDto) =>
              record.clientId ? <Link to={`/clients/${record.clientId}`}>{clientName}</Link> : clientName || '-',
          },
        ]
      : []),
    {
      title: t('projects:due-date', 'Due Date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
    },
    {
      title: t('projects:priority', 'Priority'),
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: number | null) => (
        <Tag color={getPriorityColor(priority)}>
          {t(`projects:priority-${getPriorityName(priority).key}`, getPriorityName(priority).title)}
        </Tag>
      ),
    },
    {
      title: t('projects:status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: ProjectPlanStatus) => {
        return (
          <Tag color={getProjectPlanStatusColor(status)}>
            {t(
              `projects:plan-status-${getProjectPlanStatusName(status)?.key!}`,
              getProjectPlanStatusName(status)?.title!,
            )}
          </Tag>
        );
      },
    },
    {
      title: t('projects:created-info', 'Created'),
      key: 'createdInfo',
      render: (_: any, record: ProjectPlanDto) => (
        <span>
          {formatDate(record.createdAt)} by {record.createdByName}
        </span>
      ),
    },
    {
      title: t('projects:actions', 'Actions'),
      key: 'actions',
      render: (_: any, record: ProjectPlanDto) => {
        const handleStatusChange = async (newStatus: ProjectPlanStatus) => {
          try {
            await projectPlansApi.apiProjectsProjectIdPlansIdStatusPut({
              projectId: projectId!,
              id: record.id!,
              updateProjectPlanStatusCommand: {
                id: record.id!,
                status: newStatus as ProjectPlanStatus,
              },
            });

            if (!initialPlans?.length) {
              // If no initialPlans, fetch fresh data
              fetchPlans();
            } else {
              // If we have initialPlans, update the status locally
              setPlans(currentPlans =>
                currentPlans.map(plan => (plan.id === record.id ? { ...plan, status: newStatus } : plan)),
              );
            }

            openNotificationWithIcon('success', t('projects:status-update-success', 'Status updated successfully'));
          } catch (err) {
            openNotificationWithIcon('error', t('projects:status-update-error', 'Failed to update status'));
          }
        };

        return (
          <Space>
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    disabled
                    style={{
                      cursor: 'default',
                      color: '#000000',
                      fontWeight: 500,
                      borderBottom: '1px solid #e8e8e8',
                      marginBottom: '4px',
                    }}
                  >
                    {t('projects:change-status', 'Change Status')}
                  </Menu.Item>
                  {Object.values(ProjectPlanStatus).map(status => (
                    <Menu.Item
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      disabled={record.status === status}
                    >
                      {t(
                        `projects:plan-status-${getProjectPlanStatusName(status)?.key!}`,
                        getProjectPlanStatusName(status)?.title!,
                      )}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <Button type="link" icon={<FeatherIcon icon="more-vertical" size={16} />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      {!hideAddButton && (
        <Button
          type="primary"
          icon={<FeatherIcon icon="plus" size={16} />}
          onClick={() => setIsAddModalVisible(true)}
          style={{ marginBottom: 16 }}
        >
          {t('projects:add-plan', 'Add New Plan')}
        </Button>
      )}

      <AddProjectPlanModal
        visible={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        onSubmit={handleAddPlan}
        projectId={projectId!}
      />

      <Table loading={loading} dataSource={plans} columns={columns} rowKey="id" pagination={false} />
    </>
  );
};

export default ProjectPlansTable;
